/**
 * ChatInput Component
 * Text input area for sending messages to the AI.
 * Supports Enter to send, Shift+Enter for new lines.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAIContext } from './AIContext';

const ChatInput: React.FC = () => {
  const { sendMessage, isProcessing } = useAIContext();
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  // Focus input when chat opens
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleSubmit = async () => {
    if (!input.trim() || isProcessing) return;
    const message = input;
    setInput('');
    await sendMessage(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div
      className="p-3 border-t"
      style={{
        borderColor: 'var(--grey-light)',
        paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0.75rem))',
      }}
    >
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask me anything about your finances..."
          rows={1}
          disabled={isProcessing}
          className="flex-1 resize-none rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
          style={{
            backgroundColor: 'var(--bg-grey-light)',
            color: 'var(--dark)',
            border: '1px solid transparent',
            minHeight: '40px',
            maxHeight: '120px',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--primary)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'transparent';
          }}
        />
        <button
          onClick={handleSubmit}
          disabled={!input.trim() || isProcessing}
          className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200 disabled:opacity-40"
          style={{
            backgroundColor: 'var(--primary)',
            color: '#FFFFFF',
          }}
          aria-label="Send message"
        >
          {isProcessing ? (
            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5m0 0l-7 7m7-7l7 7" />
            </svg>
          )}
        </button>
      </div>
      <div
        className="mt-1 text-[10px] text-center opacity-50"
        style={{ color: 'var(--grey-dark)' }}
      >
        Press Enter to send · Shift+Enter for new line
      </div>
    </div>
  );
};

export default ChatInput;
